import React from 'react';
import { Link } from 'react-router-dom';

import { DatePickerField, MCButton, MCText, SearchTextField, View } from 'src/components/basic';
import { TableLibrary } from 'src/components/complex';
import BaseComponent from 'src/components/BaseComponent';
import ShortInfo from 'src/components/user/ShortInfo';

import {
  addressObject,
  formatDate,
  lang,
  permissions,
  remCss,
  scssVariables,
  withRouter
} from 'src/helpers';

import constants from 'src/constants';

import model from './model';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import HospitalSelect from 'src/components/complex/HospitalSelect';
import AppointmentStatus from 'src/components/appointments/AppointmentStatus';
import ButtonDropdown from 'src/components/complex/ButtonDropdown';
import LANG_CONST from 'src/helpers/lang/const';
import AlertManager from 'src/components/alert/AlertManager';

const cellProps = {
  style: {
    alignItems: 'flex-start'
  }
};

class Appointment extends BaseComponent {
  componentDidMount() {
    model.setStateSilent({ pageIndex: 0 });
    model.subscribe(this.subscription);
  }

  componentWillUnmount() {
    model.unsubscribe(this.subscription);
  }

  renderDetailActions = (item) => {
    const partner = item.partner;
    const params = { auth: model.state.user, appointment: item };
    const { PAGES, ACTIONS } = permissions;
    const medium = !this.props.screenDetail?.isLargeScreen;

    return (
      <>
        <div className={'mc-flex mc-flex-start'}>
          <div className={'mc-grow mc-flex-col'}>
            {medium && (
              <>
                <div className={'mc-flex-0'}>
                  <MCText label={LANG_CONST.diagnostic} />
                  <MCText label={': ' + item.data?.disease} flex />
                </div>
                <div className={'mc-flex-0'}>
                  <MCText label={LANG_CONST.date} />
                  <MCText label={': ' + formatDate(item.data.startDate)} flex />
                </div>
              </>
            )}
          </div>
          <div className={`${medium ? 'mc-flex' : 'mc-flex-5-col'} mc-flex-start`}>
            {ACTIONS.APPOINTMENTS_CONFIRM_BUTTON.checkActive(params) && (
              <ButtonDropdown
                width={146}
                menu={(toggle) => (
                  <>
                    <MCButton
                      {...ACTIONS.APPOINTMENTS_CONFIRM_BUTTON.attributes}
                      medium
                      hover
                      style={{ width: '100%' }}
                      radius={false}
                      onClick={(e) => {
                        e.preventDefault();
                        model.updateStatus(item, constants.general.appointmentStatus.not_attended);
                        toggle();
                      }}
                      dark={item.data.status === constants.general.appointmentStatus.not_attended}
                      disabled={
                        item.data.status === constants.general.appointmentStatus.not_attended
                      }
                      label={LANG_CONST.buttons.not_attended}
                    />
                    {ACTIONS.APPOINTMENTS_CANCEL_BUTTON.checkActive(params) && (
                      <MCButton
                        {...ACTIONS.APPOINTMENTS_CANCEL_BUTTON.attributes}
                        medium
                        style={{ width: '100%' }}
                        hover
                        radius={false}
                        onClick={() => {
                          model.showCancel(item);
                          toggle();
                        }}
                      />
                    )}
                  </>
                )}
                mainButton={() => (
                  <MCButton
                    {...ACTIONS.APPOINTMENTS_CONFIRM_BUTTON.attributes}
                    medium
                    hover
                    radius={false}
                    style={{ border: 'none', width: remCss(146 - 42) }}
                    onClick={(e) => {
                      e.preventDefault();
                      model.updateStatus(item, constants.general.appointmentStatus.attended);
                    }}
                    dark={item.data.status === constants.general.appointmentStatus.attended}
                    disabled={item.data.status === constants.general.appointmentStatus.attended}
                    label={LANG_CONST.buttons.attended}
                  />
                )}
              />
            )}
            {ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.checkActive(params) && (
              <MCButton
                {...ACTIONS.APPOINTMENTS_RESCHEDULE_BUTTON.attributes}
                type={'link'}
                style={{ width: remCss(150) }}
                medium
                to={PAGES.APPOINTMENTS_RESCHEDULE_PAGE.match(item.id)}
              />
            )}
            {ACTIONS.APPOINTMENTS_REBOOK_BUTTON.checkActive(params) && (
              <MCButton
                {...ACTIONS.APPOINTMENTS_REBOOK_BUTTON.attributes}
                type={'link'}
                style={{ width: remCss(150) }}
                medium
                to={
                  model.state.user?.isClinic || model.state.user?.isManager
                    ? PAGES.CLINIC_APPOINTMENTS_REBOOK_PAGE.match(item.id)
                    : model.state.user?.isDoctor
                      ? PAGES.PATIENTS_APPOINTMENT_BOOK_PAGE.match(partner?.id)
                      : PAGES.APPOINTMENTS_BOOK_PAGE.match(partner?.username)
                }
              />
            )}
            {!ACTIONS.APPOINTMENTS_CONFIRM_BUTTON.checkActive(params) &&
              ACTIONS.APPOINTMENTS_CANCEL_BUTTON.checkActive(params) && (
                <MCButton
                  {...ACTIONS.APPOINTMENTS_CANCEL_BUTTON.attributes}
                  medium
                  style={{ width: remCss(150) }}
                  onClick={() => model.showCancel(item)}
                />
              )}
          </div>
          <Link className={'mc-no-style-btn'} to={PAGES.APPOINTMENTS_DETAIL_PAGE.match(item.id)}>
            <SVGIcon
              iconname={BASE_ICONS.EYE_FILL}
              fill={scssVariables['colors-mc-primary']}
              fillOpacity={'0.5'}
              width={42}
              dark
              padding={5}
              radius
            />
          </Link>
        </div>
      </>
    );
  };

  render() {
    let loading = model.refreshing();
    let noResults = model.noResults();
    let searchQuery = model.listInput('search');
    let items = model.items().filter((item) => !!item.data && !!item.data.startDate);
    let { total = 0 } = model.getCurrent();
    const { PAGES, ACTIONS } = permissions;
    let showNotificationCount = false;

    const medium = !this.props.screenDetail?.isLargeScreen;

    const columns = [
      {
        label: !model.getUser()?.isPatient ? LANG_CONST.patients : LANG_CONST.doctors,
        resize: true,
        sort: { sortKey: !model.getUser()?.isPatient ? 'patient' : 'doctor' },
        cellProps: { style: { alignItems: 'flex-start' } },
        renderCell: (item) => {
          const partner =
            (!model.getUser()?.isPatient ? item.patient : item.partner) || item.partner || {};
          return (
            partner?.id && (
              <ShortInfo user={partner} to={permissions.profilePath(partner)} vertical />
            )
          );
        }
      },
      !medium && {
        label: LANG_CONST.diagnostic,
        resize: true,
        sort: { sortKey: 'disease' },
        cellProps,
        renderCell: (item) => item.data?.disease
      },

      !medium && {
        label: LANG_CONST.date,
        resize: true,
        cellProps,
        sort: { sortKey: 'startDate' },
        renderCell: (item) => {
          if (!item.data?.startDate) return '';

          return (
            <View style={{ gap: remCss(5) }}>
              <span>{formatDate(item.data.startDate)}</span>
              {showNotificationCount && item.notificationCount && model.getUser()?.isPatient && (
                <MCText
                  textLight
                  weight={500}
                  label={lang.t(LANG_CONST.text.sms_notifications, { num: item.notificationCount })}
                />
              )}
            </View>
          );
        }
      },
      !model.getUser()?.isDoctor &&
        !model.getUser()?.isPatient && {
          label: LANG_CONST.doctors,
          resize: true,
          sort: { sortKey: 'doctor' },
          cellProps: { style: { alignItems: 'flex-start' } },
          renderCell: ({ doctor }) => {
            return <ShortInfo user={doctor} to={permissions.profilePath(doctor)} vertical />;
          }
        },

      !model.getUser()?.isPatient && {
        label: (
          <HospitalSelect
            template={6}
            placeholder={LANG_CONST.location}
            expandOptions={[{ id: 'virtual', hospital: 'Virtual' }]}
            apiConfig={{
              url: 'hospital/list',
              activeAppointments: true,
              clinicIds: model.state.user?.clinicIds,
              hospitalIds: model.state.user?.hospitalIds
            }}
            name={'hospitalId'}
            value={model.input('hospitalId')}
            onChange={(n, v) => {
              if (v?.toString() === model.input('hospitalId')?.toString()) return;
              model.setStateSilent({
                pageIndex: 0,
                [n]: v
              });
              model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
              model.refresh();
            }}
          />
        ),
        resize: true,
        cellProps,
        renderCell: (item) => {
          if (item.data.appointmentType === 'virtual') return 'Virtual';
          if (!item.data.hospital) return '';

          return (
            <MCText
              numberOfLines={3}
              label={
                item.data.hospital?.split('_')[0] +
                ' ' +
                addressObject({ cityAddress: item.data.cityAddress }).fullAddressNameReverse
              }
              heading
              size={18}
            />
          );
        }
      },
      {
        label: LANG_CONST.status,
        resize: true,
        width: `${remCss(150)}px`,
        sort: { sortKey: 'status' },
        cellProps,
        renderCell: (item) => (
          <div className={'mc-flex-col'}>
            <AppointmentStatus appointment={item} />
            {ACTIONS.APPOINTMENTS_CONTINUE_BUTTON.checkActive({
              auth: model.state.user,
              appointment: item
            }) && (
              <MCButton
                {...ACTIONS.APPOINTMENTS_CONTINUE_BUTTON.attributes}
                medium
                onClick={() => model.showContinue(item, this.props.router)}
              />
            )}
          </div>
        )
      },

      !medium && {
        label: LANG_CONST.actions,
        cellStyles: { textAlign: 'right' },
        cellProps,
        width: `${remCss(230)}px`,
        renderCell: this.renderDetailActions
      }
    ].filter((c) => !!c);

    return (
      <div className={'mc-container-content mc-appointment'}>
        <div className={'mc-container-content__header mc-flex-15'}>
          <span className={'mc-grow'}>{lang.t(LANG_CONST.titles.appointments)}</span>
          <SearchTextField
            width={300}
            template={'2'}
            onChange={(text) => {
              model.setStateSilent({
                pageIndex: 0
              });
              model.setListStateSilent({ items: [], total: 0 }, 'search');
              model.handleChangeSearch(text);
              if (!text) {
                model.setListState({ items: [] }, model.getCurrentId(), null, true);
                model.refresh();
              }
            }}
            value={model.listInput('search')}
          />
          <DatePickerField
            style={{ maxWidth: remCss(200) }}
            styleBox={{ backgroundColor: 'white' }}
            template={'6'}
            icontype={2}
            placeholder={LANG_CONST.select_date}
            placement={'bottom-end'}
            name={'startDate'}
            value={model.input('startDate')}
            onChange={(n, v) => {
              if (v?.toString() === model.input('startDate')?.toString()) return;
              model.setStateSilent({
                pageIndex: 0,
                [n]: v
              });
              model.setListStateSilent({ items: [], total: 0 }, model.getCurrentId());
              model.refresh();
            }}
            invalid={{}}
          />
          {!model.state.user.isPatient && (
            <MCButton
              onClick={() => {
                if (model.state.user?.status === constants.general.userStatus.disabled) {
                  return model.emit('checkDisabledAccount', model.state.user);
                }
                if (
                  !model.state.user?.isDoctor &&
                  !permissions.PAGES.APPOINTMENTS_CREATE_PAGE.isActive
                ) {
                  return AlertManager.popup(AlertManager.TYPES.INFO, {
                    width: remCss(650),
                    title: LANG_CONST.manager_no_permissions.appointments,
                    actionButton: {
                      label: LANG_CONST.buttons.ok,
                      action: ({ hide }) => hide()
                    }
                  });
                }
                this.navigate(PAGES.APPOINTMENTS_CREATE_PAGE.path);
              }}
              primary
              medium
              style={{ height: remCss(50) }}
              label={LANG_CONST.buttons.add_appointment}
              radius
              light
            />
          )}
          {(model.state.user?.isClinic || model.state.user?.isManager) && (
            <MCButton
              type={'link'}
              to={'/schedule'}
              primary
              medium
              style={{ height: remCss(30) }}
              label={LANG_CONST.buttons.doctors_appointments}
              radius
              dark
            />
          )}
        </div>
        <div className={'mc-container-content__body'}>
          <div className={'mc-container-content__body-content'} style={{ marginTop: remCss(25) }}>
            {model.state.user?.id && (
              <TableLibrary
                key={model.state.user.id + '_' + (medium ? 1 : 0)}
                loading={loading}
                renderExtend={medium && this.renderDetailActions}
                className={medium ? 'mc-row2' : ''}
                noResults={noResults}
                searchQuery={searchQuery}
                sortBy={model.state.sortBy}
                total={total}
                limit={model.listLimit}
                selectedPage={model.state.pageIndex}
                onSortChange={(sortBy) => {
                  model.handleChange('sortBy', sortBy);
                  model.setListState({ items: [] }, model.getCurrentId(), null, true);
                  model.loadMore();
                }}
                onSetPage={(v) => {
                  model.handleChange('pageIndex', v);
                  model.setListState({ items: [] }, model.getCurrentId(), null, true);
                  model.loadMore();
                }}
                columns={columns}
                data={items}
              />
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(Appointment);
